import { useMemo } from "react";
import { format } from "date-fns";
import { Tooltip } from "../Tooltip/Tooltip";
import {
  MESSAGE_STATUS_CONFIG,
  StatusHistoryItem,
} from "./constants";

interface MessageStatusIndicatorProps {
  statusHistory: StatusHistoryItem[];
}

const MessageStatusIndicator = ({
  statusHistory,
}: MessageStatusIndicatorProps) => {
  const latestStatus = useMemo(() => {
    if (!statusHistory || statusHistory.length === 0) {
      return { status: "delivered", timestamp: new Date().toISOString() };
    }
    const sorted = [...statusHistory].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    return sorted[0];
  }, [statusHistory]);

  const readEntries = useMemo(() => {
    return statusHistory.filter((item) => item.status === "read");
  }, [statusHistory]);

  if (!latestStatus) return null;

  const config = MESSAGE_STATUS_CONFIG[latestStatus.status];
  if (!config) return null;

  const StatusIcon = config.icon;
  const readCount = readEntries.length;
  const isReadWithMultiple =
    latestStatus.status === "read" && readCount > 1;

  const displayLabel =
    latestStatus.status === "read" && readCount > 1
      ? `Read (${readCount})`
      : config.label;

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "MMM d, h:mm a");
    } catch {
      return timestamp;
    }
  };

  const renderTooltipContent = () => {
    if (isReadWithMultiple) {
      const sortedReads = [...readEntries].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      return (
        <div className="text-sm leading-normal">
          <p className="mb-1">
            Read by the recipient{" "}
            <span className="font-semibold">{readCount} times</span>
          </p>
          <ul className="list-disc ml-4 max-h-[150px] overflow-y-auto">
            {sortedReads.map((entry, idx) => (
              <li key={idx}>
                {formatTimestamp(entry.timestamp)}
                {idx === 0 && (
                  <span className="italic text-gray-placeholder ml-1">
                    (Most recent)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return `${config.label} at ${formatTimestamp(latestStatus.timestamp)}`;
  };

  return (
    <Tooltip content={renderTooltipContent()} side="top">
      <div className="flex items-center gap-[2px] cursor-default">
        <StatusIcon
          className={`w-[11px] h-[11px] ${config.color} ${config.darkColor}`}
        />
        <p
          className={`text-sm font-medium leading-none tracking-[-0.22px] whitespace-nowrap ${config.color} ${config.darkColor}`}
        >
          {displayLabel}
        </p>
      </div>
    </Tooltip>
  );
};

export default MessageStatusIndicator;
